/**
 * Auth Client - External OAuth2 Integration
 * Connects to https://auth.1anime.app for authentication
 *
 * NOTE: This temporarily disables the local Better Auth system
 * and uses external OAuth2 authentication instead.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  SESSION_COOKIE_NAME,
  type AuthScope,
  type ExternalAuthUser,
} from './external-auth';

// Session state for React components
interface SessionState {
  data: {
    user: ExternalAuthUser;
    session: {
      expiresAt: string;
      createdAt: string;
      updatedAt: string;
    };
  } | null;
  isPending: boolean;
  error: Error | null;
}

export const ANILIST_RELINK_REQUIRED_EVENT = 'anilist-relink-required';

interface AniListRelinkRequiredDetail {
  message?: string;
  status?: number;
}

export function shouldPromptAniListRelink(input: {
  message?: string;
  status?: number;
}): boolean {
  if (input.status === 400 || input.status === 401) {
    return true;
  }

  const normalizedMessage = input.message?.toLowerCase() || '';

  return [
    'token',
    'unauthorized',
    'authentication',
    'invalid token',
    'expired',
  ].some((fragment) => normalizedMessage.includes(fragment));
}

export function notifyAniListRelinkRequired(
  detail: AniListRelinkRequiredDetail = {}
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AniListRelinkRequiredDetail>(
      ANILIST_RELINK_REQUIRED_EVENT,
      {
        detail,
      }
    )
  );
}

/**
 * React hook for managing user session
 * Compatible with the previous useSession interface
 */
export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({
    data: null,
    isPending: true,
    error: null,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchSession = async (): Promise<void> => {
      try {
        const response = await fetch('/api/auth/session', {
          credentials: 'include',
        });

        if (!isMounted) return;

        if (response.ok) {
          const sessionData = await response.json();
          setState({
            data: sessionData,
            isPending: false,
            error: null,
          });
        } else {
          setState({
            data: null,
            isPending: false,
            error: null,
          });
        }
      } catch (error) {
        if (!isMounted) return;
        setState({
          data: null,
          isPending: false,
          error:
            error instanceof Error
              ? error
              : new Error('Failed to fetch session'),
        });
      }
    };

    fetchSession();

    return () => {
      isMounted = false;
    };
  }, []);

  return state;
}

function isSafeRedirectPath(value: string | undefined): value is string {
  if (!value) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.startsWith('/\\')) return false;
  return true;
}

/**
 * Sign in - redirects to external OAuth provider
 */
export const signIn = {
  /**
   * Redirect to external OAuth login via /api/auth/start, which sets the
   * state cookie used by the callback to verify the OAuth round-trip.
   * `scopes` is no longer plumbed through here — defaults are baked into
   * the server side of the flow.
   */
  oauth: (options?: { scopes?: AuthScope[]; redirectTo?: string }): void => {
    const params = new URLSearchParams();
    const redirectTo = options?.redirectTo;
    if (typeof redirectTo === 'string' && isSafeRedirectPath(redirectTo)) {
      params.set('redirectTo', redirectTo);
    }
    // Pass the browser's origin so the api builds a redirect_uri that
    // points back here, not at whatever BETTER_AUTH_URL is configured
    // to on the server. Without this the OAuth round-trip can land on
    // a different origin from where the state cookie was set, which
    // surfaces as "invalid_state" on the sign-in page.
    if (typeof window !== 'undefined' && window.location?.origin) {
      params.set('webOrigin', window.location.origin);
    }
    const qs = params.toString();
    window.location.href = qs ? `/api/auth/start?${qs}` : '/api/auth/start';
  },

  /**
   * Email sign in with OTP-based authentication
   * Uses better-auth email OTP flow via auth server
   */
  email: async (options: {
    email: string;
    password: string;
    callbackURL?: string;
  }): Promise<{ error?: { message: string } }> => {
    try {
      // Use local API endpoint that proxies to auth server
      const response = await fetch('/api/auth/sign-in-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: options.email,
          password: options.password,
        }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          error: {
            message: data.error_description || data.error || 'Sign in failed',
          },
        };
      }

      // Redirect after successful sign in
      if (options.callbackURL) {
        window.location.href = options.callbackURL;
      } else {
        window.location.reload();
      }

      return { error: undefined };
    } catch (error) {
      return {
        error: {
          message: error instanceof Error ? error.message : 'Sign in failed',
        },
      };
    }
  },

  /**
   * Social sign in - redirects to OAuth
   */
  social: (options: { provider: string; callbackURL?: string }): void => {
    signIn.oauth({ redirectTo: options.callbackURL });
  },
};

/**
 * Sign out - clears session and redirects
 */
export async function signOut(options?: {
  redirectTo?: string;
}): Promise<void> {
  const authBaseUrl = 'https://auth.1anime.app';
  const redirectTarget = options?.redirectTo || window.location.href;
  const encodedRedirect = encodeURIComponent(redirectTarget);

  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Ignore errors during logout
  }

  // Clear client-side session cookie (better-auth format)
  document.cookie = `${SESSION_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  // Also clear legacy cookie name for backward compatibility
  document.cookie = `1anime_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

  window.location.href = `${authBaseUrl}/logout?redirect=${encodedRedirect}`;
}

/**
 * Sign up with email and password
 */
export const signUp = {
  email: async (options: {
    email: string;
    password: string;
    name?: string;
    callbackURL?: string;
  }): Promise<{ error?: { message: string } }> => {
    try {
      // Use local API endpoint that proxies to auth server
      const response = await fetch('/api/auth/sign-up-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: options.email,
          password: options.password,
          name: options.name,
        }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          error: {
            message: data.error_description || data.error || 'Sign up failed',
          },
        };
      }

      // Redirect after successful sign up
      if (options.callbackURL) {
        window.location.href = options.callbackURL;
      } else {
        window.location.reload();
      }

      return { error: undefined };
    } catch (error) {
      return {
        error: {
          message: error instanceof Error ? error.message : 'Sign up failed',
        },
      };
    }
  },
};

/**
 * Auth client object for compatibility with existing code
 */
export const authClient = {
  signIn,
  signOut,
  signUp,
  useSession,

  /**
   * Get current session (for non-React contexts)
   */
  getSession: async (): Promise<SessionState['data']> => {
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });

      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch {
      return null;
    }
  },

  /**
   * List linked accounts (fetches from local database via API)
   * Returns accounts in a format compatible with the old Better Auth interface
   */
  listAccounts: async (): Promise<{
    data: Array<{
      providerId: string;
      accountId: string;
      accessToken?: string;
    }>;
  }> => {
    try {
      // Fetch both anilist status and token in parallel
      const [statusRes, tokenRes] = await Promise.all([
        fetch('/api/auth/anilist/status', { credentials: 'include' }),
        fetch('/api/auth/get-anilist-token', { credentials: 'include' }),
      ]);

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        if (statusData.connected && statusData.userId) {
          let accessToken: string | undefined;
          if (tokenRes.ok) {
            const tokenData = await tokenRes.json();
            accessToken = tokenData.accessToken;
          }
          return {
            data: [
              {
                providerId: 'anilist',
                accountId: statusData.userId,
                accessToken,
              },
            ],
          };
        }
      }
      return { data: [] };
    } catch {
      return { data: [] };
    }
  },

  /**
   * Link AniList account through the local app so tokens are stored in our database.
   */
  linkAnilist: (callbackURL?: string): void => {
    const returnUrl = callbackURL || window.location.href;
    const encodedReturnUrl = encodeURIComponent(returnUrl);
    const webOrigin = encodeURIComponent(window.location.origin);
    window.location.href = `/api/auth/anilist/connect?redirect=${encodedReturnUrl}&webOrigin=${webOrigin}`;
  },

  /**
   * Relink AniList account - use when AniList token has expired
   */
  relinkAnilist: (callbackURL?: string): void => {
    const returnUrl = callbackURL || window.location.href;
    const encodedReturnUrl = encodeURIComponent(returnUrl);
    const webOrigin = encodeURIComponent(window.location.origin);
    window.location.href = `/api/auth/anilist/connect?redirect=${encodedReturnUrl}&webOrigin=${webOrigin}`;
  },

  /**
   * Unlink a social account from the current user's profile
   */
  unlinkAccount: async (
    provider: 'discord' | 'google' | 'anilist'
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`/api/auth/unlink?provider=${provider}`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error_description || data.error || 'Unlink failed',
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unlink failed',
      };
    }
  },

  /**
   * Revoke access for a specific OAuth client/app
   */
  revokeClient: async (
    clientId: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/auth/revoke', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clientId }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error_description || data.error || 'Revoke failed',
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Revoke failed',
      };
    }
  },

  /**
   * Register a new OAuth application
   */
  registerApp: async (
    accessToken: string,
    appData: {
      name: string;
      redirect_uris: string[];
      scopes: string[];
    }
  ): Promise<{
    client_id: string;
    client_secret: string;
    name: string;
    redirect_uris: string[];
    scopes: string[];
  }> => {
    const { registerApp } = await import('./external-auth');
    return registerApp(accessToken, appData);
  },
};

/**
 * Hook to get session refresh function
 */
export function useSessionRefresh(): () => Promise<void> {
  const refreshSession = useCallback(async () => {
    // Trigger a session refresh by calling the session endpoint
    await fetch('/api/auth/session?refresh=true', {
      credentials: 'include',
    });
  }, []);

  return refreshSession;
}
